import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import Alert from 'core/components/Alert'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import React, { useCallback, useEffect } from 'react'
import { toggleDeleteOnTermination } from './actions'
import { IVirtualMachinesSelector } from './newModel'

const useStyles = makeStyles<Theme>((theme) => ({
  'top-spacing': {
    marginTop: theme.spacing(2),
  },
  'bottom-spacing': {
    marginBottom: theme.spacing(2),
  },
}))

const { nova } = ApiClient.getInstance()

const DeleteOnTerminationDialog = ({
  rows: [vm],
  onClose,
}: {
  rows: IVirtualMachinesSelector[]
  onClose: (success: boolean) => void
}) => {
  const classes = useStyles({
    theme: {},
  })
  const { update, updating, error } = useUpdateAction(toggleDeleteOnTermination)
  const [loading, setLoading] = React.useState(true)
  const [volume, setVolume] = React.useState(null)

  // Fetch volume attachments
  useEffect(() => {
    if (!vm?.id || volume) return

    const getVolumeAttachments = async () => {
      try {
        setLoading(true)
        const volumeAttachments = await nova.getVolumeAttachments({ vmId: vm?.id })
        setVolume(
          volumeAttachments?.find(
            (volume) => volume?.device === vm?.['OS-EXT-SRV-ATTR:root_device_name'],
          ),
        )
      } finally {
        setLoading(false)
      }
    }

    getVolumeAttachments()
  }, [vm?.id])

  const vmName = vm.name || vm.id
  const deleteOnTermination = volume?.delete_on_termination
  const action = deleteOnTermination ? 'Disable' : 'Enable'

  const handleConfirm = useCallback(async () => {
    const body = {
      volumeAttachment: {
        volumeId: volume?.volumeId,
        delete_on_termination: !volume?.delete_on_termination,
      },
    }

    const { success } = await update({
      id: vm?.id,
      volumeId: volume?.volumeId,
      body,
    })
    if (success) onClose(true)
  }, [vm?.id, volume?.volumeId, update, onClose])

  return (
    <ModalForm
      panel="dialog"
      open
      onClose={onClose}
      title={`Root Volume Delete on VM deletion`}
      data-testid="delete-on-termination-modal"
      error={error}
      onSubmit={handleConfirm}
      submitting={updating}
      submitTitle={deleteOnTermination ? 'Disable' : 'Enable'}
      maxWidth={600}
      loading={loading}
      disableSubmit={loading || !volume}
    >
      {!loading && !volume ? (
        <Text variant="body2" className={classes['bottom-spacing']}>
          No root volume found for VM {vmName}
        </Text>
      ) : (
        <>
          <Text variant="body2" className={classes['bottom-spacing']}>
            Delete on VM deletion is currently{' '}
            <strong>{deleteOnTermination ? 'enabled' : 'disabled'}</strong> for the root volume on
            the VM <strong>{vmName}</strong>.
          </Text>

          <Text variant="body2">
            You are about to <strong>{action.toLowerCase()}</strong> the setting to{' '}
            <strong>delete the root volume</strong> when VM <strong>{vmName}</strong> is deleted.
          </Text>

          {!deleteOnTermination && (
            <Alert
              variant="warning"
              className={classes['top-spacing']}
              message={
                <Text variant="body2">
                  Warning: Enabling this option means the root volume will be{' '}
                  <strong>permanently deleted</strong> if the VM is terminated. Ensure this is
                  intended, as recovery will not be possible.{' '}
                </Text>
              }
            />
          )}

          {deleteOnTermination && (
            <Text variant="body2" className={classes['top-spacing'] || ''}>
              You can re-attach the volume manually in the future if you choose not to delete it
              during VM deletion.
            </Text>
          )}
        </>
      )}
    </ModalForm>
  )
}

export default DeleteOnTerminationDialog
